/**
 * cart-drawer-a11y.js
 * WS5.1 / F-4.1.2-e — Cart drawer dialog focus management.
 *
 * The custom cart drawer (#cart-drawer) opens by toggling an `active` class
 * from MULTIPLE code paths (cart-drawer.js openDrawer/closeDrawer, the cart
 * icon toggle, and product-form.js updateAndOpenCartDrawer on the live PDP
 * add-to-cart path because no Dawn <cart-drawer> element exists on this site).
 * None of those paths move focus into the drawer, trap focus, make the page
 * behind it inert, or return focus on close. Result: a keyboard / screen
 * reader user must traverse the entire underlying page before the drawer is
 * reachable.
 *
 * This controller is a single, path-independent fix: a MutationObserver on
 * #cart-drawer's `class` attribute reacts to the `active` state changing no
 * matter which code toggled it. It does NOT modify the existing CartDrawer
 * class, does NOT wrap fetch / /cart/add.js, and adds no second behavior.
 *
 * Static dialog semantics (role="dialog", aria-modal, aria-labelledby,
 * tabindex="-1") live in snippets/cart-drawer.liquid on the persistent outer
 * element (only the drawer's innerHTML is swapped by the theme, never the
 * outer node, so those attributes survive cart refreshes).
 *
 * Idempotent: guarded by a window flag so a double include cannot double-bind.
 */
(function () {
  'use strict';

  if (window.__cartDrawerA11yLoaded) return;
  window.__cartDrawerA11yLoaded = true;

  var DRAWER_ID = 'cart-drawer';
  var ACTIVE_CLASS = 'active';
  var INERT_MARK = 'data-cd-inert';
  var ARIA_MARK = 'data-cd-aria-hidden';
  var SKIP_TAGS = { SCRIPT: 1, LINK: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1 };
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var isOpen = false;
  var previouslyFocused = null;
  var keydownBound = null;
  var attrObserver = null;
  var observedNode = null;

  // A5 fix pass (2026-05-18): in-drawer quantity / remove focus restoration.
  // Changing quantity (or remove) makes the theme replace the drawer's
  // innerHTML via the Section Rendering API, destroying the focused control
  // so focus drops to <body> until the next Tab. We record the last-focused
  // such control and, after the re-render, restore focus to its equivalent
  // in the new DOM. Each control carries data-line (1-based forloop.index);
  // the kind is read from its class. Purely additive to WS5; does not modify
  // the CartDrawer class and does not wrap fetch.
  var contentObserver = null;
  var focusinBound = null;
  var lastDrawerControl = null;
  var restoreScheduled = false;
  var QTY_REMOVE_SEL =
    '.cart-drawer__quantity-decrease, .cart-drawer__quantity-increase, ' +
    '.cart-drawer__quantity-input, .cart-drawer__remove';
  var KIND_SEL = {
    decrease: '.cart-drawer__quantity-decrease',
    increase: '.cart-drawer__quantity-increase',
    input: '.cart-drawer__quantity-input',
    remove: '.cart-drawer__remove'
  };

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function isVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  function getFocusable(drawer) {
    var nodes = drawer.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      if (isVisible(nodes[i]) && nodes[i].getAttribute('aria-hidden') !== 'true') {
        out.push(nodes[i]);
      }
    }
    return out;
  }

  function findTrigger() {
    // Best guess at the control that opened the drawer, used for focus return.
    var ae = document.activeElement;
    var drawer = getDrawer();
    if (
      ae &&
      ae !== document.body &&
      ae.isConnected &&
      (!drawer || !drawer.contains(ae))
    ) {
      return ae;
    }
    return (
      document.querySelector('[data-cart-toggle]') ||
      document.querySelector('a[href="/cart"]') ||
      document.querySelector('a[href*="/cart"]') ||
      null
    );
  }

  function setBackgroundInert(on) {
    var drawer = getDrawer();
    var overlay = document.querySelector('.cart-drawer-overlay');
    if (on) {
      var kids = document.body.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el === drawer || el === overlay) continue;
        // The cart status live region (WS5.2) is a direct <body> child; it
        // must stay perceivable so the "Added … to cart" announcement is not
        // suppressed by inert/aria-hidden while the drawer is open.
        if (el.id === 'CartStatusLiveRegion') continue;
        if (SKIP_TAGS[el.tagName]) continue;
        if (el.hasAttribute(INERT_MARK)) continue;
        el.setAttribute(INERT_MARK, '');
        try {
          el.inert = true;
        } catch (e) {
          /* inert unsupported: aria-hidden below still removes it from the a11y tree */
        }
        if (el.getAttribute('aria-hidden') !== 'true') {
          el.setAttribute(ARIA_MARK, '');
          el.setAttribute('aria-hidden', 'true');
        }
      }
    } else {
      var marked = document.querySelectorAll('[' + INERT_MARK + ']');
      for (var j = 0; j < marked.length; j++) {
        var m = marked[j];
        try {
          m.inert = false;
        } catch (e2) {}
        m.removeAttribute(INERT_MARK);
        if (m.hasAttribute(ARIA_MARK)) {
          m.removeAttribute('aria-hidden');
          m.removeAttribute(ARIA_MARK);
        }
      }
    }
  }

  function closeDrawer() {
    // Reuse the theme's own close logic; fall back progressively.
    if (window.cartDrawerInstance && typeof window.cartDrawerInstance.closeDrawer === 'function') {
      window.cartDrawerInstance.closeDrawer();
      return;
    }
    var closeBtn = document.querySelector('#' + DRAWER_ID + ' .cart-drawer__close');
    if (closeBtn) {
      closeBtn.click();
      return;
    }
    var drawer = getDrawer();
    var overlay = document.querySelector('.cart-drawer-overlay');
    if (drawer) drawer.classList.remove(ACTIVE_CLASS);
    if (overlay) overlay.classList.remove(ACTIVE_CLASS);
    document.body.style.overflow = '';
  }

  function onKeydown(e) {
    if (!isOpen) return;
    var drawer = getDrawer();
    if (!drawer) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.key !== 'Tab') return;

    var focusable = getFocusable(drawer);
    if (focusable.length === 0) {
      // Nothing focusable inside: keep focus on the dialog container itself.
      e.preventDefault();
      drawer.focus();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (!drawer.contains(active)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function controlKind(el) {
    if (!el || !el.classList) return null;
    if (el.classList.contains('cart-drawer__quantity-decrease')) return 'decrease';
    if (el.classList.contains('cart-drawer__quantity-increase')) return 'increase';
    if (el.classList.contains('cart-drawer__quantity-input')) return 'input';
    if (el.classList.contains('cart-drawer__remove')) return 'remove';
    return null;
  }

  function onFocusin(e) {
    if (!isOpen) return;
    var drawer = getDrawer();
    if (!drawer || !e.target || !drawer.contains(e.target)) return;
    var ctrl = e.target.closest ? e.target.closest(QTY_REMOVE_SEL) : null;
    var kind = controlKind(ctrl);
    if (!kind) return;
    // A5+ : capture the product title now (the Remove path destroys the line
    // on re-render, so it cannot be read back afterward). Prefer the
    // data-item-title attribute on the line; fall back to the visible title
    // text so this still degrades gracefully on a not-yet-repushed theme.
    var itemEl = ctrl.closest ? ctrl.closest('.cart-drawer__item') : null;
    var title = '';
    if (itemEl) {
      title = itemEl.getAttribute('data-item-title') || '';
      if (!title) {
        var t = itemEl.querySelector('.cart-drawer__item-title');
        title = t ? (t.textContent || '').replace(/\s+/g, ' ').trim() : '';
      }
    }
    lastDrawerControl = { kind: kind, line: ctrl.getAttribute('data-line'), title: title };
  }

  // A5+ fix pass (2026-05-19) — SC 4.1.3 Status Messages (AA).
  // WS5.2's #CartStatusLiveRegion is scoped to the add-to-cart pub/sub only,
  // so an in-drawer quantity/remove change produced NO status message: the
  // restored control's accessible name (now product-specific in
  // cart-drawer.liquid) was the only cue, and for Remove the line is gone so
  // focus falls back to the close button with no product context at all.
  // Reuse the SAME single region (no second live region): the qty/remove
  // path uses /cart/change.js from the CartDrawer class and never publishes
  // `cart-update`, so it cannot collide with the add path. Clear-then-set
  // mirrors WS5.2 so a repeated identical message still re-announces.
  function announceDrawerChange() {
    if (!lastDrawerControl) return;
    var region = document.getElementById('CartStatusLiveRegion');
    if (!region) return;
    var drawer = getDrawer();
    if (!drawer) return;
    var title = lastDrawerControl.title || 'Item';
    var msg;
    if (lastDrawerControl.kind === 'remove') {
      msg = title + ' removed from cart';
    } else {
      var line = lastDrawerControl.line;
      var lv = window.CSS && CSS.escape ? CSS.escape(line) : line;
      var input =
        (line != null && drawer.querySelector(KIND_SEL.input + '[data-line="' + lv + '"]')) ||
        drawer.querySelector(KIND_SEL.input);
      if (!input) return; // line gone unexpectedly: stay silent, do not lie
      var qty = (input.value || '').toString().trim();
      if (!qty) return;
      msg = title + ', quantity ' + qty;
    }
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = msg;
    }, 60);
  }

  function restoreDrawerFocus() {
    restoreScheduled = false;
    if (!isOpen || !lastDrawerControl) return;
    var drawer = getDrawer();
    if (!drawer) return;
    // A5+ : announce the result before the focus-state guard below, so a
    // future path that keeps focus inside the drawer still gets the status.
    announceDrawerChange();
    // Only intervene if the re-render actually dropped focus out of the drawer
    // (do not steal focus the user has deliberately moved elsewhere in it).
    var ae = document.activeElement;
    if (ae && ae !== document.body && drawer.contains(ae)) return;

    var sel = KIND_SEL[lastDrawerControl.kind];
    var target = null;
    if (sel && lastDrawerControl.line != null) {
      var lv =
        window.CSS && CSS.escape ? CSS.escape(lastDrawerControl.line) : lastDrawerControl.line;
      target = drawer.querySelector(sel + '[data-line="' + lv + '"]');
    }
    if (!target && sel) target = drawer.querySelector(sel); // same kind, any line
    if (!target) target = drawer.querySelector('.cart-drawer__close');
    if (!target) {
      var f = getFocusable(drawer);
      target = f.length ? f[0] : drawer;
    }
    if (target && typeof target.focus === 'function') target.focus();
  }

  function scheduleRestore() {
    if (restoreScheduled) return;
    restoreScheduled = true;
    window.requestAnimationFrame(restoreDrawerFocus);
  }

  function handleOpen() {
    if (isOpen) return;
    isOpen = true;

    previouslyFocused = findTrigger();
    setBackgroundInert(true);

    keydownBound = onKeydown;
    document.addEventListener('keydown', keydownBound, true);

    focusinBound = onFocusin;
    document.addEventListener('focusin', focusinBound, true);

    // Wait a frame: openDrawer() swaps innerHTML then adds `active` on a
    // short timeout, so the close button exists by the time we focus.
    window.requestAnimationFrame(function () {
      var drawer = getDrawer();
      if (!drawer) return;
      var closeBtn = drawer.querySelector('.cart-drawer__close');
      var focusable = getFocusable(drawer);
      if (closeBtn && isVisible(closeBtn)) {
        closeBtn.focus();
      } else if (focusable.length) {
        focusable[0].focus();
      } else {
        drawer.focus();
      }
    });
  }

  function handleClose() {
    if (!isOpen) return;
    isOpen = false;

    setBackgroundInert(false);

    if (keydownBound) {
      document.removeEventListener('keydown', keydownBound, true);
      keydownBound = null;
    }

    if (focusinBound) {
      document.removeEventListener('focusin', focusinBound, true);
      focusinBound = null;
    }
    lastDrawerControl = null;

    var target = previouslyFocused;
    previouslyFocused = null;
    if (target && target.isConnected && typeof target.focus === 'function' && isVisible(target)) {
      target.focus();
    }
  }

  function syncState() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (drawer.classList.contains(ACTIVE_CLASS)) {
      handleOpen();
    } else {
      handleClose();
    }
  }

  function observe(drawer) {
    if (attrObserver) attrObserver.disconnect();
    observedNode = drawer;
    attrObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'class') {
          syncState();
          break;
        }
      }
    });
    attrObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // A5: watch the drawer subtree; the quantity/remove re-render swaps the
    // drawer's innerHTML. Only act when the drawer is open AND a qty/remove
    // control was the last focused thing (so this never fires on the open
    // render, which focuses the close button instead).
    if (contentObserver) contentObserver.disconnect();
    contentObserver = new MutationObserver(function () {
      if (isOpen && lastDrawerControl) scheduleRestore();
    });
    contentObserver.observe(drawer, { childList: true, subtree: true });

    syncState();
  }

  function init() {
    var drawer = getDrawer();
    if (drawer) observe(drawer);

    // The outer #cart-drawer node should persist (theme only swaps its
    // innerHTML), but guard against a wholesale replacement: if the node
    // identity changes, re-attach the attribute observer.
    var bodyObserver = new MutationObserver(function () {
      var current = getDrawer();
      if (current && current !== observedNode) observe(current);
    });
    bodyObserver.observe(document.body, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
